// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import Pages
import { FileInputMateDrive, RadioButtons, Checkboxes, TabPanel } from '../../../FlowConfigurationInputs';
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import VariablePicker from '../../../FlowConfigurationInputs/VariablePicker/VariablePicker';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';

// Import Recoil
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { saveAvailable, initialActions, changedFields } from "../../../../recoil-store/recoil-state";

// Import Helpers
import { CheckIfFlowUpdated } from '../../../../helpers/helper';

/**
 * @Description This function lets you return component according to the name of the incoming action.
 * @Param       action - object
 * @Param       variables - object variable list
 * @Param       setOpenVariable - open variable function that allows us to open
 * @param       showAlert - show alert
 * @Returns     Component
 * @Author      ibrahim AKIN
 */
function Read({ action, showAlert, setOpenVariable, variables, automId }) {
    const { t } = useTranslation();
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);

    const [value, setValue] = useState(action.fields?.fileType?.value === 'TEXT' ? 1 : 0);
    const label = 'CONTENT.CONFIGURATION-FORM.EXCEL.OPEN.';
    const csvLabel = 'CONTENT.CONFIGURATION-FORM.CSV.';
    const options = {
        fileInput: {
            checkExtension: (ext) => {
                if (ext !== 'csv') {
                    showAlert(t('CONTENT.CONFIGURATION-FORM.CSV.READ.UPLOAD-CSV-ONLY'));
                    return false;
                }
                return true;
            },
            fileType: '.csv'
        },
        radioButtons: {
            data: [{
                value: 'ro',
                label: label + 'READ-ONLY'
            }, {
                value: 'rw',
                label: label + 'READ-WRITE'
            }],
            defaultValue: 'ro'
        },
        commaButtons: {
            data: [{
                value: ',',
                label: csvLabel + 'COMMA'
            }, {
                value: ';',
                label: csvLabel + 'SEMI-COLON'
            }],
            defaultValue: ','
        },
        checkboxes: { label: label + 'SHEET-CONTAINS-A-HEADER' },
        utf8: { label: csvLabel + 'READ-FILE-AS-UTF8' }

    }




    useEffect(() => {
        if (!action.fields.fileType) {
            action.fields.fileType = { value: "FILE" };
        } else {
            setValue(action.fields.fileType.value === "FILE" ? 0 : 1);
        }
    }, [action, variables])

    function handleChangeTabs(newValue) {
        if (newValue === 1) {
            action.fields.filePath.type = 'TEXT';
            action.fields.fileType.value = 'TEXT';
            action.fields.filePath.backup = { value: action.fields.filePath.value };
            action.fields.filePath.value = "";
            if (action.fields.filePath.backupText?.value) {
                action.fields.filePath.value = action.fields.filePath.backupText.value;
            }
        } else {
            // action.fields.filePath.type = 'FILE';
            action.fields.fileType.value = 'FILE';

            action.fields.filePath.backupText = { value: action.fields.filePath.value }
            delete action.fields.filePath.value
            if (action.fields.filePath.backup?.value) {
                action.fields.filePath.value = action.fields.filePath.backup.value;
            }
        }

        let v = newValue === 1 ? 'TEXT' : 'FILE';
        const field = initialActionsData[action.id]?.fields.fileType;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated("fileType", action, oldValue, v, setSaveAvailable, setChangeFields, changedFieldsState);
        setValue(newValue);
    }

    return (
        <div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'FILE-PATH')} is_required={true} />
                <div className="condition-tabs">
                    <button
                        className={`f-s-11 ${value === 0 ? 'condition-active' : ''}`}
                        onClick={() => handleChangeTabs(0)}>
                        {t(label + 'SELECT')}
                    </button>
                    <button
                        className={`f-s-11 ${value === 1 ? 'condition-active' : ''}`}
                        onClick={() => handleChangeTabs(1)}>
                        {t(label + 'CUSTOM')}
                    </button>
                </div>
                <TabPanel value={value} index={0}>
                    <FileInputMateDrive action={action} inputName="filePath" options={options.fileInput} isRequired
                        isSize={true} hideRequired={true} autom_id={automId} />
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <TextInput action={action} inputName="filePath" isRequired={false} setOpenVariable={setOpenVariable}
                        variables={variables} fieldType="string" displayName={t(label + 'FILE-PATH')} hideRequired={true} autom_id={automId} />
                </TabPanel>
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(csvLabel + 'DELIMITER')} is_required={false} />
                <RadioButtons action={action} inputName="delimiter" options={options.commaButtons} isRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'OPEN-AS')} />
                <RadioButtons action={action} inputName="openAs" options={options.radioButtons} isRequired />
            </div>
            <div className="conf-field-container">
                <Checkboxes action={action} inputName="sheetContains" options={options.checkboxes} />
            </div>
            <div className="conf-field-container">
                <Checkboxes action={action} inputName="utf8" options={options.utf8} />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t('CONTENT.CONFIGURATION-FORM.CSV.READ.ASSIGN-DATA')} is_required={true} />
                <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable} filter="data table" isRequired hideRequired={true} />
                <small className='conf-field-desc'><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'data table' })}</em></small>
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t('CONTENT.CONFIGURATION-FORM.CSV.READ.ASSIGN-PATH')} />
                <VariablePicker action={action} inputName="assignToVariable" variables={variables} setOpenVariable={setOpenVariable} filter="string" hideRequired={true} />
                <small className='conf-field-desc'><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'string' })}</em></small>
            </div>
        </div>
    )
}

export default Read;